import React from "react";
import { api, photoUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Trash2, UploadCloud } from "lucide-react";

export default function AdminPhotos() {
  const [photos, setPhotos] = React.useState([]);
  const [files, setFiles] = React.useState([]);
  const [caption, setCaption] = React.useState("");
  const [category, setCategory] = React.useState("gallery");
  const [order, setOrder] = React.useState(0);
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState({ done: 0, total: 0 });

  const load = () => api.get("/photos").then((r) => setPhotos(r.data || []));
  React.useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error("Choose at least one file");
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    let okCount = 0;
    let failCount = 0;
    let firstError = "";
    // Upload sequentially so order increments are predictable and we can track progress
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const fd = new FormData();
      fd.append("file", f);
      fd.append("caption", caption);
      fd.append("category", category);
      fd.append("order", String(Number(order) + i));
      try {
        await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        okCount += 1;
      } catch (err) {
        failCount += 1;
        if (!firstError) firstError = err?.response?.data?.detail || "Upload failed";
      }
      setProgress({ done: i + 1, total: files.length });
    }
    if (okCount) toast.success(`${okCount} photo${okCount > 1 ? "s" : ""} uploaded`);
    if (failCount) toast.error(`${failCount} failed${firstError ? `: ${firstError}` : ""}`);
    setFiles([]);
    setCaption("");
    setOrder(0);
    e.target.reset();
    setUploading(false);
    setProgress({ done: 0, total: 0 });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    await api.delete(`/photos/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-photos-page">
      <div className="mb-8">
        <p className="overline mb-2">Visual Library</p>
        <h1 className="font-serif-display text-4xl">Photos</h1>
      </div>

      <form
        onSubmit={upload}
        className="border border-[var(--border)] rounded-2xl p-6 mb-10 bg-[#13151D]"
        data-testid="photo-upload-form"
      >
        <h3 className="font-serif-display text-xl mb-4">Upload New Photo</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
              Files {files.length > 0 && <span className="text-[#C5A059] normal-case tracking-normal">({files.length} selected)</span>}
            </Label>
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="photo-file-input"
            />
            <p className="text-xs text-[#A1A4B5] mt-1">
              Tip: Hold Ctrl/Cmd or Shift to select multiple photos at once.
            </p>
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Caption (optional, applies to all)</Label>
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="photo-caption-input"
            />
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-2 bg-[#0A0B10] border-[var(--border)]" data-testid="photo-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#13151D] border-[var(--border)]">
                <SelectItem value="gallery">Gallery</SelectItem>
                <SelectItem value="hero">Hero</SelectItem>
                <SelectItem value="room">Room</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Starting Order</Label>
            <Input
              type="number"
              value={order}
              onChange={(e) => setOrder(Number(e.target.value))}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="photo-order-input"
            />
          </div>
        </div>
        <Button
          type="submit"
          disabled={uploading}
          className="gold-btn rounded-full mt-6 px-6"
          data-testid="photo-upload-submit"
        >
          <UploadCloud size={16} className="mr-2" />
          {uploading
            ? `Uploading… ${progress.done}/${progress.total}`
            : files.length > 1
            ? `Upload ${files.length} Photos`
            : "Upload Photo"}
        </Button>
      </form>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {photos.length === 0 && (
          <p className="text-[#A1A4B5] col-span-full">No photos yet — upload your first one.</p>
        )}
        {photos.map((p) => (
          <div key={p.id} className="relative group rounded-xl overflow-hidden border border-[var(--border)]" data-testid={`photo-card-${p.id}`}>
            <img src={photoUrl(p.id)} alt={p.caption} className="w-full aspect-[4/3] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-4 flex flex-col justify-end">
              <div className="text-xs text-[#C5A059] uppercase tracking-[0.2em]">{p.category}</div>
              <div className="text-sm">{p.caption || p.original_filename}</div>
              <Button
                size="sm"
                onClick={() => remove(p.id)}
                className="mt-3 self-start bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/40"
                data-testid={`delete-photo-${p.id}`}
              >
                <Trash2 size={12} className="mr-1" /> Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
